import { useLayoutEffect, useRef, useState } from 'react';

import { boardGrid, keyWidthPx, keyboardMetrics, type KeyboardMetrics } from '../core/profile/cssVars';
import { getLayer, getLayout, keyLabel, keyName, keySpan, type LayoutKey } from '../core/profile/layouts';
import { useProfileStore } from '../core/profile/ProfileStore';
import { useUiStore } from '../app/uiStore';
import { ActivateButton } from './ActivateButton';

/**
 * The keyboard.
 *
 * **Spans the full width, deliberately.** §5.2's principle is that recall-driven elements may
 * span the screen while search-driven ones must not: the person does not visually hunt for "S",
 * because spelling is intact and QWERTZ is decades of muscle memory. So spatial spread is
 * acceptable here even under a field loss — and the extra width buys larger keys and larger gaps,
 * both of which help.
 *
 * Sizing measures the container rather than assuming 1180 pt, because the true usable width
 * depends on the safe-area insets and those are what broke the original budget.
 *
 * ── Two boards, one grid (GEO-03, GEO-19, ADR-039) ──
 *
 * The digit layer is drawn by this component from the same data as the letters, and the grid it is drawn into
 * is **the letter board's**, via `boardGrid`. The layer has two rows where its board has three, so measuring
 * it honestly would give it bigger keys wherever height binds — and the promise the whole layer is built
 * around is that a key is the same size in the same place whichever board is showing.
 */

interface KeyGridProps {
  onKey: (key: string) => void;
}

export function KeyGrid({ onKey }: KeyGridProps): React.JSX.Element {
  const settings = useProfileStore((s) => s.settings);
  const keyboardLayer = useUiStore((s) => s.keyboardLayer);
  // The letter board is the geometry even when the layer is what gets drawn — never `board` below.
  const letterBoard = getLayout(settings.keyboardLayout);
  const board = keyboardLayer === 'digits' ? getLayer(settings.keyboardLayout) : letterBoard;
  const containerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measure = (): void =>
      setBox({ width: element.clientWidth, height: element.clientHeight });
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const metrics = box
    ? keyboardMetrics({
        keyMm: settings.keyMm,
        keyGapMm: settings.keyGapMm,
        pxPerMm: settings.pxPerMm,
        availableWidthPx: box.width,
        availableHeightPx: box.height,
        ...boardGrid(letterBoard),
      })
    : null;

  // Publish the effective size so the "nothing smaller than a key is interactive" rule (§5.2)
  // is measured against what is on screen rather than what was requested.
  useLayoutEffect(() => {
    if (metrics) document.documentElement.style.setProperty('--key-size', `${metrics.keyPx}px`);
  }, [metrics?.keyPx]);

  return (
    <div
      ref={containerRef}
      data-testid="key-grid"
      style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      {metrics
        ? board.rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: `${metrics.gapXPx}px`,
                marginTop: rowIndex === 0 ? 0 : `${metrics.gapYPx}px`,
              }}
            >
              {row.map((layoutKey) => (
                <Key key={layoutKey.key} layoutKey={layoutKey} metrics={metrics} onKey={onKey} />
              ))}
            </div>
          ))
        : null}
    </div>
  );
}

/**
 * One key.
 *
 * Activation goes through the registry rather than an `onClick` of its own, so the protections live
 * in the adapter where every input mode inherits them: release within target, slop tolerance,
 * tremor debounce, touch-offset correction (§5.1).
 */
function Key({
  layoutKey,
  metrics,
  onKey,
}: {
  layoutKey: LayoutKey;
  metrics: KeyboardMetrics;
  onKey: (key: string) => void;
}): React.JSX.Element {
  return (
    <ActivateButton
      id={`key:${layoutKey.key}`}
      onActivate={() => onKey(layoutKey.key)}
      label={keyName(layoutKey)}
      style={{
        width: keyWidthPx(keySpan(layoutKey), metrics),
        height: metrics.keyPx,
        // Overrides the global --min-target floor: a key is the thing that floor is derived from.
        minWidth: 0,
        minHeight: 0,
        // Sized from the height, not the width: a wider key is not a bigger letter, and `␣` scaled to a
        // double-width key would be a glyph nobody asked for.
        fontSize: `${Math.round(metrics.keyPx * 0.38)}px`,
        background: 'var(--key-bg)',
        color: 'var(--key-fg)',
        border: '2px solid var(--key-border)',
        borderRadius: 'var(--radius)',
      }}
    >
      {keyLabel(layoutKey)}
    </ActivateButton>
  );
}
